import {
  CLERK_PRIMARY_ORIGIN,
  CLU_APP_ORIGIN,
  IS_CLERK_SATELLITE,
} from '@/components/themed-clerk-provider.component';
import { useClerk } from '@clerk/react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { SIGN_UP_PATH, ticketNeedsSignUp } from '@/lib/clerk-ticket';

// Module-level lock so that a second render pass (or StrictMode double-fire)
// can't try to re-consume the same single-use ticket.
const inFlightTickets = new Set<string>();

const bailToLogin = (url: URL) => {
  // Strip the ticket from the URL so we don't loop, and hard-navigate to
  // /auth/login so the "Signing you in..." loader doesn't stick forever.
  url.searchParams.delete('__clerk_ticket');
  window.location.replace('/auth/login');
};

// Clerk refuses every sign-in on the Clu satellite (app.clugtm.com), tickets
// included. Consume the ticket on the primary's sign-in page, the one path
// Canada isn't redirected away from (public/_redirects), and come back here
// with __clerk_synced=false so the satellite pulls the new session over, as
// Clerk's own satellite sign-in does.
const forwardTicketToPrimary = (url: URL, ticket: string) => {
  url.searchParams.delete('__clerk_ticket');
  url.searchParams.set('__clerk_synced', 'false');
  const primary = new URL('/auth/login', CLERK_PRIMARY_ORIGIN);
  primary.searchParams.set('__clerk_ticket', ticket);
  primary.searchParams.set('redirect_url', url.toString());
  window.location.replace(primary.toString());
};

// On the primary: where to go once the ticket is consumed. Only ever back to
// the satellite, so the ticket link can't send anyone to another site.
const satelliteReturnUrl = (url: URL): string | null => {
  const target = url.searchParams.get('redirect_url');
  if (!target) return null;
  try {
    return new URL(target).origin === CLU_APP_ORIGIN ? target : null;
  } catch {
    return null;
  }
};

// An invitee without an account accepts on the sign-up page, where Clerk's
// <SignUp/> consumes the ticket. Signing in with it only answers "User not
// found", which used to bounce new invitees to the login page. Sign-up lives
// on the primary, so the satellite sends them there too.
const signUpUrl = (url: URL) => {
  const next = new URL(url.toString());
  next.searchParams.set('__clerk_status', 'sign_up');
  const origin = IS_CLERK_SATELLITE ? CLERK_PRIMARY_ORIGIN : url.origin;
  return new URL(SIGN_UP_PATH + next.search, origin).toString();
};
const sendToSignUp = (url: URL) => window.location.replace(signUpUrl(url));

export const ClerkTicketHandler = () => {
  const clerk = useClerk();
  const handledRef = useRef(false);

  // Depend on `clerk.loaded` specifically — `clerk` itself is a stable
  // reference, so without this dep the effect would only fire once (at
  // mount) and never re-run when Clerk finishes loading.
  useEffect(() => {
    if (!clerk.loaded || handledRef.current) return;

    const url = new URL(window.location.href);
    const ticket = url.searchParams.get('__clerk_ticket');
    if (!ticket) return;

    if (inFlightTickets.has(ticket)) {
      handledRef.current = true;
      return;
    }

    if (ticketNeedsSignUp(url.search)) {
      handledRef.current = true;
      // Signed in as someone else: the invitee's sign-up needs no session.
      if (clerk.session) {
        void clerk.signOut({
          sessionId: clerk.session.id,
          redirectUrl: signUpUrl(url),
        });
        return;
      }
      // Already there: <SignUp/> takes the ticket from the URL.
      if (url.pathname !== SIGN_UP_PATH || IS_CLERK_SATELLITE)
        sendToSignUp(url);
      return;
    }

    if (IS_CLERK_SATELLITE) {
      handledRef.current = true;
      inFlightTickets.add(ticket);
      forwardTicketToPrimary(url, ticket);
      return;
    }

    const handleTicket = async () => {
      try {
        if (clerk.session) {
          handledRef.current = true;
          inFlightTickets.add(ticket);
          // redirectUrl: Clerk navigates after sign-out, to "/" by default,
          // which in Canada now leaves for app.clugtm.com without the ticket.
          await clerk.signOut({
            sessionId: clerk.session.id,
            redirectUrl: url.toString(),
          });
          // Keep the ticket in the URL across the reload so the next mount
          // (now signed-out) can consume it via signIn.create.
          window.location.assign(url.toString());
          return;
        }

        handledRef.current = true;
        inFlightTickets.add(ticket);

        const result = await clerk.client.signIn.create({
          strategy: 'ticket',
          ticket,
        });

        if (result.status === 'complete' && result.createdSessionId) {
          await clerk.setActive({ session: result.createdSessionId });
          const backToSatellite = satelliteReturnUrl(url);
          url.searchParams.delete('__clerk_ticket');
          window.location.assign(backToSatellite ?? url.toString());
          return;
        }

        console.error('Clerk ticket sign-in did not complete.', result.status);
        toast.error('Impersonation ticket could not be consumed.');
        bailToLogin(url);
      } catch (error: unknown) {
        const clerkError = (
          error as {
            errors?: {
              code?: string;
              message?: string;
              longMessage?: string;
            }[];
          }
        )?.errors?.[0];
        const errCode = clerkError?.code;
        if (errCode === 'session_exists') {
          await clerk.signOut({ redirectUrl: url.toString() });
          window.location.assign(url.toString());
          return;
        }
        // An invitation link without __clerk_status, opened by someone who
        // has no account yet.
        if (errCode === 'form_identifier_not_found') {
          sendToSignUp(url);
          return;
        }
        if (clerkError) {
          // Clerk's own answer about this link (revoked, already accepted,
          // banned): the user's situation, not a bug, so show it and warn.
          console.warn('Clerk ticket was refused.', errCode);
          toast.error(
            clerkError.longMessage ??
              clerkError.message ??
              "We couldn't sign you in with this link."
          );
        } else {
          console.error('Failed to sign in with Clerk ticket.', error);
          toast.error("We couldn't sign you in with this link.");
        }
        bailToLogin(url);
      }
    };

    void handleTicket();
  }, [clerk, clerk.loaded]);

  return null;
};
